import { useEffect, useMemo, useRef, useState } from "react";
import {
  ArrowRight,
  MessageSquareText,
  Paperclip,
  Scale,
  ScrollText,
  Send,
} from "lucide-react";
import { listDadnoThreads, type DadnoThreadSummary } from "../lib/dadno-chat-store";
import { READER_TOOLS, type ToolId } from "../lib/readerTools";

const DADNO_LOGO = "/brand/dadno-logo.png";

export type DadnoHubMode = "summary" | "risk" | "consult";

const AI_TOOL_IDS: ToolId[] = ["summary", "legal-risk", "assistant"];

const MODE_TO_TOOL: Record<Exclude<DadnoHubMode, "consult">, ToolId> = {
  summary: "summary",
  risk: "legal-risk",
};

const CONSULT_SEED =
  "سلام؛ لطفاً به‌عنوان مشاور حقوقی هوشمند دادنو به من مشاوره بده.";

type Props = {
  onBack: () => void;
  onSelectAiTool: (id: ToolId) => void;
  onStartChat: (seed: {
    message: string;
    attachmentName?: string;
    mode?: DadnoHubMode;
  }) => void;
  initialMode?: DadnoHubMode | null;
};

export function DadnoHubPage({
  onBack,
  onSelectAiTool,
  onStartChat,
  initialMode = null,
}: Props) {
  const [message, setMessage] = useState("");
  const [attachmentName, setAttachmentName] = useState<string | null>(null);
  const [history, setHistory] = useState<DadnoThreadSummary[]>([]);
  const handledInitialMode = useRef(false);

  useEffect(() => {
    setHistory(listDadnoThreads());
  }, []);

  useEffect(() => {
    if (!initialMode || handledInitialMode.current) return;
    handledInitialMode.current = true;
    if (initialMode === "consult") {
      onStartChat({ message: CONSULT_SEED, mode: "consult" });
      return;
    }
    onSelectAiTool(MODE_TO_TOOL[initialMode]);
  }, [initialMode, onSelectAiTool, onStartChat]);

  const tools = useMemo(
    () => READER_TOOLS.filter((t) => AI_TOOL_IDS.includes(t.id)),
    [],
  );

  function send() {
    const text = message.trim();
    if (!text) return;
    onStartChat({
      message: text,
      attachmentName: attachmentName ?? undefined,
      mode: "consult",
    });
  }

  function openTool(id: ToolId) {
    if (id === "assistant") {
      onStartChat({ message: CONSULT_SEED, mode: "consult" });
      return;
    }
    onSelectAiTool(id);
  }

  const toolLabel = (id: ToolId) => {
    if (id === "summary") return "خلاصه اسناد حقوقی";
    if (id === "legal-risk") return "تحلیل ریسک قراردادها";
    if (id === "assistant") return "مشاوره هوشمند";
    return READER_TOOLS.find((t) => t.id === id)?.label ?? id;
  };

  return (
    <div className="flow-page dadno-hub consumer-shell">
      <header className="flow-page-head">
        <button type="button" className="text-back" onClick={onBack}>
          <ArrowRight size={16} />
          بازگشت
        </button>
        <div className="dadno-title-lockup">
          <img src={DADNO_LOGO} alt="دادنو۷" className="dadno-brand-logo" />
          <h1>دادنو۷</h1>
        </div>
        <div className="dashboard-feature-chips dadno-head-chips" aria-label="قابلیت‌ها">
          <span className="dashboard-feature-chip">خلاصه</span>
          <span className="dashboard-feature-chip">ریسک</span>
          <span className="dashboard-feature-chip">مشاوره</span>
        </div>
      </header>

      <section className="dadno-tools" aria-label="ابزارهای هوش مصنوعی">
        <h2>ابزارها</h2>
        <div className="home-tools-grid">
          {tools.map((t) => {
            const Icon =
              t.id === "summary" ? ScrollText : t.id === "legal-risk" ? Scale : null;
            return (
              <button
                key={t.id}
                type="button"
                className="home-tool-card"
                onClick={() => openTool(t.id)}
              >
                {Icon ? (
                  <Icon size={18} />
                ) : (
                  <img
                    src={DADNO_LOGO}
                    alt=""
                    className="dadno-tool-logo"
                    width={22}
                    height={10}
                  />
                )}
                {toolLabel(t.id)}
              </button>
            );
          })}
        </div>
      </section>

      <section className="dadno-compose" aria-label="پیام جدید">
        <h2>گفتگوی جدید</h2>
        <textarea
          rows={3}
          dir="rtl"
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          placeholder="سؤال حقوقی یا درخواست خود را بنویسید…"
        />
        <div className="dadno-compose-bar">
          <label className="ghost-btn attach-label">
            <Paperclip size={15} />
            پیوست سند
            <input
              type="file"
              hidden
              onChange={(e) => {
                const f = e.target.files?.[0];
                setAttachmentName(f?.name ?? null);
              }}
            />
          </label>
          {attachmentName ? (
            <span className="settings-hint">{attachmentName}</span>
          ) : null}
          <button
            type="button"
            className="primary-btn"
            disabled={!message.trim()}
            onClick={send}
          >
            <Send size={15} />
            ارسال
          </button>
        </div>
      </section>

      <section className="dadno-history" aria-label="تاریخچه گفتگو">
        <h2>تاریخچه</h2>
        {!history.length ? (
          <p className="dashboard-list-empty">هنوز گفتگویی ثبت نشده است.</p>
        ) : (
          <ul className="local-draft-list">
            {history.map((t) => (
              <li key={t.id}>
                <button
                  type="button"
                  onClick={() =>
                    onStartChat({
                      message: t.preview,
                    })
                  }
                >
                  <MessageSquareText size={14} />
                  <span>{t.title}</span>
                  <small>{new Date(t.updatedAt).toLocaleString("fa-IR")}</small>
                </button>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}
